import { ErrorMessage, Field, useField } from 'formik';
import React from 'react';

export interface FormSelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface FormSelectProps {
  name: string;
  label: string;
  options: FormSelectOption[];
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  hint?: string;
  className?: string;
  id?: string;
}

const FormSelect: React.FC<FormSelectProps> = ({
  name,
  label,
  options,
  placeholder,
  required,
  disabled,
  hint,
  className = '',
  id,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [, meta] = useField(name);
  const hasError = meta.touched && !!meta.error;
  const fieldId = id || `select-${name}`;

  return (
    <div className={className}>
      {/* Label */}
      <label
        htmlFor={fieldId}
        className={`block text-sm font-normal mb-2 ${
          hasError ? 'text-error-500' : 'text-monday-text-muted'
        }`}
      >
        {label}
        {required && <span className="form-required-dot"></span>}
      </label>

      {/* Select Container */}
      <div className="relative">
        <Field
          as="select"
          name={name}
          id={fieldId}
          disabled={disabled}
          className={`
            mobile-form-input
            w-full
            appearance-none
            pr-10
            ${hasError ? 'border-red-500' : ''}
          `}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </option>
          ))}
        </Field>

        {/* Dropdown Icon */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
          <svg
            className={`w-4 h-4 ${hasError ? 'text-error-500' : 'text-monday-text-muted'}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Error Message */}
      <ErrorMessage name={name} component="div" className="text-red-500 text-xs mt-1" />

      {/* Hint */}
      {hint && !hasError && (
        <p className="mt-1 text-xs text-monday-text-muted">{hint}</p>
      )}
    </div>
  );
};

export default FormSelect;
